// @flow

// ClickthroughLink is an abstraction over NavigateLink, navigating to the
// external link and providing a number of props that would otherwise be
// repeated.

import React, { type Node } from 'react';
import contextLogger from 'app/common/lib/ContextLogger';
import getPrefetchCompletionPercent from 'app/common/react/components/PinRep/utils/getPrefetchCompletionPercent';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import Testable from 'app/common/react/ui/Testable';
import { Box } from 'gestalt';
import { buildPinDeeplinks } from 'app/common/lib/deeplinkRoutes';
import { track } from 'app/common/lib/react/analytics';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';

type Props = {|
  children: Node,
  pin: Pin,

  // Tracking Params (override TrackingParametersContext)
  componentType?: $Values<PCONST.thrift.ComponentType>,
  contextLogData?: Object,
  elementType?: $Values<PCONST.thrift.ElementType>,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
|};

export default function ClickthroughLink({
  children,
  componentType,
  contextLogData,
  elementType,
  pin,
  viewParameter,
  viewType,
}: Props) {
  const defaultTrackingParameters = useTrackingParameters();

  const { carousel_data, id: pinId, link, tracked_link } = pin;

  let url;
  let auxData;
  let testId = 'one-tap-desktop';

  // Build the carousel slot's link. If the pin is a carousel pin, the link
  // should take the user to the current slot.
  if (carousel_data) {
    const { carousel_slots = [], id, index } = carousel_data;

    if (carousel_slots[index] && carousel_slots[index].link) {
      url = carousel_slots[index].link;
    }

    auxData = {
      carousel_slot_id: carousel_slots[index] && carousel_slots[index].id,
      carousel_data_id: id,
      carousel_slot_index: index,
    };

    testId += '-carousel';
  } else {
    url = tracked_link || link;
  }

  const handleClick = () => {
    const eventLogger = contextLogger.getInstance();
    const previousView = eventLogger.getViewFromContext();

    track(PCONST.thrift.EventType.PIN_CLICKTHROUGH, {
      ...contextLogData,
      view: viewType || defaultTrackingParameters.viewType,
      viewParameter: viewParameter || defaultTrackingParameters.viewParameter,
      component: componentType || defaultTrackingParameters.componentType,
      objectId: pinId,
      grid_index: defaultTrackingParameters.slotIndex,
      pin_id: pinId,
      prefetch_percentage_complete: getPrefetchCompletionPercent(link || ''),
    });

    track(PCONST.thrift.EventType.VIEW, {
      closeup_navigation_type: 'web_closeup',
      objectId: pinId,
      component: componentType || defaultTrackingParameters.componentType,
      view: PCONST.thrift.ViewType.PIN,
      ...defaultTrackingParameters.contextLogData,
      ...contextLogData,
    });

    // Ensure view is reset to the previous view before
    // closeup view event was emitted
    eventLogger.addViewToContext(previousView);
  };

  return (
    <Box data-test-id="otpp">
      {/* These nested test ids and cryptic names are not ideal,
        and should be consolidated as part of ads_desktop_whitespace_gaps cleanup */}
      <Testable id={testId}>
        <NavigateLink
          auxData={auxData}
          deeplinkUrls={buildPinDeeplinks(pinId)}
          external
          onClick={handleClick}
          pinId={pinId}
          style={{
            cursor: 'pointer',
            display: 'block',
          }}
          to={url}
        >
          {children}
        </NavigateLink>
      </Testable>
    </Box>
  );
}
